import React, { createContext, useState, useContext, useEffect } from "react";
import {
  ROLES,
  hasPermission as hasPermissionHelper,
} from "../permissions/permissions";
import {
  getRoleFromToken,
  isTokenExpired,
  isImpersonating as isImpersonatingToken,
  getImpersonatorId,
} from "../utils/tokenUtils";

const AuthContext = createContext(null);

const parseStoredUser = (rawUser) => {
  if (!rawUser) return null;
  try {
    return JSON.parse(rawUser);
  } catch (error) {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("token");

    if (!token || isTokenExpired(token)) {
      const adminToken = localStorage.getItem("admin_token");
      const adminUser = parseStoredUser(localStorage.getItem("admin_user"));

      if (adminToken && adminUser && !isTokenExpired(adminToken)) {
        // Se restaura antes de borrar el respaldo, nunca al revés.
        localStorage.setItem("token", adminToken);
        localStorage.setItem("user", JSON.stringify(adminUser));
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
        setUser(adminUser);
      } else {
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
      }
    } else if (storedUser) {
      setUser(parseStoredUser(storedUser));
    }
    setLoading(false);
  }, []);

  const login = (userData) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
  };

  const getCurrentRole = () => {
    return getRoleFromToken(localStorage.getItem("token"));
  };

  const isAdmin = () => {
    return getCurrentRole() === ROLES.ADMIN;
  };

  const isAuditor = () => {
    return getCurrentRole() === ROLES.AUDITOR;
  };

  const hasPermission = (permission) => {
    return hasPermissionHelper(getCurrentRole(), permission);
  };

  const startImpersonation = (impersonatedUser) => {
    login(impersonatedUser);
  };

  const stopImpersonation = () => {
    const adminToken = localStorage.getItem("admin_token");
    const adminUser = parseStoredUser(localStorage.getItem("admin_user"));

    if (!adminToken || !adminUser || isTokenExpired(adminToken)) {
      logout();
      return { success: false };
    }

    // Se restaura antes de borrar el respaldo, nunca al revés.
    localStorage.setItem("token", adminToken);
    localStorage.setItem("user", JSON.stringify(adminUser));
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    login(adminUser);

    return { success: true, adminUser };
  };

  if (loading) {
    return <div>Cargando...</div>;
  }

  const currentToken = localStorage.getItem("token");
  const role = getCurrentRole();
  const isImpersonating = isImpersonatingToken(currentToken);
  const impersonatedBy = getImpersonatorId(currentToken);

  return (
    <AuthContext.Provider
      value={{
        user,
        role,
        login,
        logout,
        isAdmin,
        isAuditor,
        hasPermission,
        isImpersonating,
        impersonatedBy,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe ser usado dentro de un AuthProvider");
  }
  return context;
};
